package music.jammers.client.shared.component

import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsPressedAsState
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.Button
import androidx.compose.material3.DatePicker
import androidx.compose.material3.DatePickerDialog
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.SelectableDates
import androidx.compose.material3.Text
import androidx.compose.material3.TimePicker
import androidx.compose.material3.rememberDatePickerState
import androidx.compose.material3.rememberTimePickerState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.LocalTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import limasoul.client.shared.generated.resources.Res
import limasoul.client.shared.generated.resources.dialog_ok
import org.jetbrains.compose.resources.stringResource

const val EVENT_DATE_FORMAT = "dd.MM.yyyy HH:mm"
const val EVENT_DETAILS_FORMAT = "dd-MM - HH:mm"

@OptIn(ExperimentalMaterial3Api::class, ExperimentalMaterial3Api::class)
@Composable
fun AppDateField(
    localDateTime: LocalDateTime,
    title: String,
    modifier: Modifier = Modifier,
    errorText: String? = null,
    onSelected: (LocalDateTime) -> Unit,
) {
    val isError = errorText?.isNotEmpty() == true

    val value = DateFormatter.format(localDateTime, EVENT_DATE_FORMAT)

    val state = remember {
        mutableStateOf(
            DateDialogState(
                value = DateDialogState.Value.CLEAR,
                localDateTime = localDateTime,
            )
        )
    }

    val source = remember {
        MutableInteractionSource()
    }

    AppTextField(
        value = value,
        placeholder = { Text(title) },
        readOnly = true,
        interactionSource = source,
        onValueChange = { },
        modifier = modifier
    )

    if (isError) {
        Text(
            text = errorText ?: "Error",
            color = MaterialTheme.colorScheme.error,
            style = MaterialTheme.typography.bodySmall,
            modifier = Modifier.padding(start = 16.dp)
        )
    } else {
        Spacer(modifier = Modifier.size(16.dp))
    }

    if (source.collectIsPressedAsState().value) {
        state.update {
            copy(
                value = DateDialogState.Value.DATE,
            )
        }
    }

    val datePickerState = rememberDatePickerState(
        initialSelectedDateMillis = localDateTime.toInstant(TimeZone.currentSystemDefault()).toEpochMilliseconds(),
        selectableDates = object : SelectableDates {
            override fun isSelectableDate(utcTimeMillis: Long): Boolean {
                return utcTimeMillis >= Clock.System.now().toEpochMilliseconds()
            }
        },
    )

    val timePickerState = rememberTimePickerState(
        initialHour = localDateTime.time.hour,
        initialMinute = localDateTime.time.minute,
    )

    if (state.value.value != DateDialogState.Value.CLEAR) {
        DatePickerDialog(
            confirmButton = {
                Button(
                    onClick = {
                        if (state.value.value == DateDialogState.Value.DATE) {
                            state.update {
                                copy(value = DateDialogState.Value.TIME)
                            }
                        } else {
                            val instant = Instant.fromEpochMilliseconds(
                                epochMilliseconds = datePickerState.selectedDateMillis ?: 0L
                            )

                            val resultDate = instant.toLocalDateTime(TimeZone.currentSystemDefault()).date
                            val resultTime = LocalTime(timePickerState.hour, timePickerState.minute, 0, 0)

                            val resultDateTime = LocalDateTime(resultDate, resultTime)

                            onSelected(
                                resultDateTime
                            )

                            state.update {
                                copy(value = DateDialogState.Value.CLEAR)
                            }
                        }
                    }
                ) {
                    Text(stringResource(Res.string.dialog_ok))
                }
            },
            onDismissRequest = {
                state.update {
                    copy(
                        value = DateDialogState.Value.CLEAR,
                    )
                }
            }
        ) {
            if (state.value.value == DateDialogState.Value.DATE) {
                DatePicker(
                    state = datePickerState,
                )
            } else {
                Column(
                    modifier = Modifier.padding(16.dp).align(Alignment.CenterHorizontally)
                ) {
                    Text(
                        text = "Select date",
                        style = MaterialTheme.typography.titleSmall
                    )

                    Spacer(modifier = Modifier.size(32.dp))

                    TimePicker(
                        state = timePickerState,
                    )
                }
            }
        }
    }
}

internal data class DateDialogState(
    val value: Value,
    val localDateTime: LocalDateTime,
) {

    enum class Value {
        CLEAR,
        DATE,
        TIME,
    }
}